import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { FramingConfig } from "building-types/framing/framingMetaDataTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type {
  CrippleStudConfig,
  ResolvedOpeningFraming,
  WallOpeningFramingConfigGroup,
} from "building-types/framing/wallFramingTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import {
  BuildingGenerationIssueType,
  type BuildingGenerationIssue,
} from "building-types/settings/buildingGenerationIssue";
import { isOpeningHeaderEnabled } from "building-types/settings/openingHeaderAssemblySettingsTools";
import type { Table } from "common/data/table";

function studMemberConfig(count: number, framingComponentId: ForeignEntityId<"FRAMING_COMPONENT">): FramingConfig {
  return {
    count,
    framingComponentId,
  };
}

function disabledMemberConfig(): FramingConfig {
  return { count: 0, disabled: true };
}

export function buildWallOpeningFramingConfigGroup(
  inheritedStudComponentId: ForeignEntityId<"FRAMING_COMPONENT">,
  resolved: ResolvedOpeningFraming | undefined,
  headerAssemblies: Table<HeaderAssemblyEntity>,
): WallOpeningFramingConfigGroup {
  const headerEnabled = isOpeningHeaderEnabled(resolved?.headerAssemblyId);
  const hasTopHeaderSillPlate = resolved?.hasTopHeaderSillPlate === true;

  const headerComponentId =
    resolved?.headerAssemblyId !== undefined && resolved?.headerAssemblyId !== null
      ? headerAssemblies[resolved.headerAssemblyId]?.headerComponentId
      : resolved?.headerComponentId;

  let headerConfig: FramingConfig;
  if (!headerEnabled) {
    headerConfig = disabledMemberConfig();
  } else if (headerComponentId !== undefined) {
    headerConfig = { count: resolved?.headerCount ?? 1, framingComponentId: headerComponentId };
  } else {
    headerConfig = { count: resolved?.headerCount ?? 1, disabled: true };
  }

  const headerSillConfig: FramingConfig = hasTopHeaderSillPlate
    ? studMemberConfig(1, inheritedStudComponentId)
    : disabledMemberConfig();

  const topCrippleStudConfig: CrippleStudConfig = {
    framingComponentId: inheritedStudComponentId,
    ...(resolved?.hasCrippleStudsAboveOpening === false ? { disabled: true } : {}),
  };
  const bottomCrippleStudConfig: CrippleStudConfig = {
    framingComponentId: inheritedStudComponentId,
    ...(resolved?.hasCrippleStudsBelowOpening === false ? { disabled: true } : {}),
  };

  return {
    headerConfig,
    kingStudConfig: studMemberConfig(resolved?.numberOfKingStuds ?? 1, inheritedStudComponentId),
    jackStudConfig: studMemberConfig(resolved?.numberOfJackStuds ?? 1, inheritedStudComponentId),
    sillPlateConfig: studMemberConfig(1, inheritedStudComponentId),
    headerSillConfig,
    topCrippleStudConfig,
    bottomCrippleStudConfig,
    hasCrippleStudEnds: resolved?.hasCrippleStudEnds ?? true,
    roughOpeningBorder: resolved?.roughOpeningBorder,
  };
}

export function pushMissingFramingComponentIssue(
  issues: BuildingGenerationIssue[],
  componentId: string,
  context: string,
): void {
  issues.push({
    type: BuildingGenerationIssueType.WALL_FRAMING_GENERATION_ERROR,
    message: `${context}: missing framing component "${componentId}"`,
  });
}

export function pushUnresolvedWallOpeningHeaderIssue(issues: BuildingGenerationIssue[], openingId: string): void {
  issues.push({
    type: BuildingGenerationIssueType.WALL_FRAMING_GENERATION_ERROR,
    message: `Opening "${openingId}" has no resolvable header assembly; header framing was skipped`,
  });
}

function validateFramingComponentId(
  framingComponents: Table<FramingComponentEntity>,
  componentId: ForeignEntityId<"FRAMING_COMPONENT">,
  issues: BuildingGenerationIssue[],
  context: string,
): boolean {
  if (framingComponents[componentId] !== undefined) {
    return true;
  }
  pushMissingFramingComponentIssue(issues, componentId, context);
  return false;
}

//collect the framing component id for validation
function collectOpeningFramingComponentIds(configGroup: WallOpeningFramingConfigGroup): string[] {
  const ids: string[] = [];
  const configs: (FramingConfig | undefined)[] = [
    configGroup.headerConfig,
    configGroup.kingStudConfig,
    configGroup.jackStudConfig,
    configGroup.sillPlateConfig,
    configGroup.headerSillConfig,
  ];
  for (const config of configs) {
    if (config !== undefined && !config.disabled && config.framingComponentId !== undefined) {
      ids.push(config.framingComponentId);
    }
  }
  if (
    configGroup.topCrippleStudConfig?.framingComponentId !== undefined &&
    !configGroup.topCrippleStudConfig.disabled
  ) {
    ids.push(configGroup.topCrippleStudConfig.framingComponentId);
  }
  if (
    configGroup.bottomCrippleStudConfig?.framingComponentId !== undefined &&
    !configGroup.bottomCrippleStudConfig.disabled
  ) {
    ids.push(configGroup.bottomCrippleStudConfig.framingComponentId);
  }
  return ids;
}

export function validateWallOpeningFramingConfigGroup(
  configGroup: WallOpeningFramingConfigGroup,
  framingComponents: Table<FramingComponentEntity>,
  issues: BuildingGenerationIssue[],
  context: string,
): void {
  for (const componentId of collectOpeningFramingComponentIds(configGroup)) {
    validateFramingComponentId(framingComponents, componentId, issues, context);
  }
}
